import {useMutation, useMutationState, useQueryClient} from '@github-ui/react-query'
import type {Task, HydratedTask, PendingTask, HydratedTaskFromServer, CreateTaskParams} from '../types/task'
import type {Session} from '../types/session'
import {useCAPIQuery} from './useCAPIQuery'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {fetchCAPI} from '../utils/fetch-capi'
import {useState, useMemo} from 'react'
import {usePullRequests} from './usePullRequests'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IdToPullRequestMap} from '../types/pull'

function sortTasks(sessions: Session[]): Task[] {
  const resourceIdToTaskMap = new Map<string, Task>()

  for (const session of sessions) {
    const task = resourceIdToTaskMap.get(session.resource_id.toString())
    if (task) {
      task.session_count += 1
      if (new Date(session.last_updated_at) > new Date(task.latest_session_updated_at)) {
        task.latest_session_updated_at = session.last_updated_at
        task.latest_session_state = session.state
      }
    } else {
      resourceIdToTaskMap.set(session.resource_id.toString(), {
        resource_type: session.resource_type,
        resource_id: session.resource_id,
        session_count: 1,
        latest_session_updated_at: session.last_updated_at,
        latest_session_state: session.state,
      })
    }
  }

  return Array.from(resourceIdToTaskMap.values()).sort((a, b) => {
    return new Date(b.latest_session_updated_at).getTime() - new Date(a.latest_session_updated_at).getTime()
  })
}

function hydrateTasks(tasks: Task[], idToPullRequestMap: IdToPullRequestMap): HydratedTask[] {
  const hydratedTasks: HydratedTask[] = []
  if (tasks && idToPullRequestMap) {
    for (const task of tasks) {
      const pull = idToPullRequestMap[task.resource_id]
      if (!pull) continue
      hydratedTasks.push({
        pull,
        ...task,
      })
    }
  }
  return hydratedTasks
}

export function useTasks({
  apiURL,
  sessionsPollingInterval = 3000,
  onCreateError = {},
  initialTasks,
  initialPullRequestsMap,
}: {
  apiURL?: string | null
  sessionsPollingInterval?: number | false
  initialTasks?: Task[]
  initialPullRequestsMap?: IdToPullRequestMap
  onCreateError?: {
    onError?: (error: Error, variables: CreateTaskParams) => void
  }
} = {}) {
  const isBranchSelectionEnabled = isFeatureEnabled('copilot_immersive_agent_branch_selection')

  // We can remove this state once pull requests are returned within the sessions API response
  const [manuallyAddedTasks, setManuallyAddedTasks] = useState<HydratedTask[]>([])
  const queryClient = useQueryClient()

  const createSessionMutation = useMutation<HydratedTask, Error, CreateTaskParams>({
    mutationKey: ['createSession'],
    mutationFn: async ({nwo, problemStatement, baseRef}: CreateTaskParams) => {
      const response = await verifiedFetchJSON(`/${nwo}/agent-sessions`, {
        method: 'POST',
        body: {
          problem_statement: problemStatement,
          ...(isBranchSelectionEnabled && {base_ref: baseRef}),
        },
      })

      const data: HydratedTaskFromServer = (await response.json()).hydrated_task

      return {
        ...data,
        pull: {
          ...data.pull,
          id: data.pull.id,
          reviewable_state: data.pull.draft ? 'draft' : 'ready',
          url: data.pull.url,
          labels: data.pull.labels || [],
          comments: data.pull.comments || 0,
          assignees_avatar_urls: (data.pull.assignees || []).map(a => a.avatar_url),
          created_at: data.pull.created_at,
          updated_at: data.pull.updated_at,
          merged_at: data.pull.merged_at,
          closed_at: data.pull.closed_at,
          author: data.pull.user?.login,
          head_sha: data.pull.head.sha,
          repository_nwo: data.pull.base.repo.full_name,
        },
      } as HydratedTask
    },
    onSuccess: data => {
      setManuallyAddedTasks(prev => [data, ...prev])

      // Invalidate and refetch tasks for real backend. Polling will also pick up the new data, but this ensures a more immediate UI update.
      queryClient.invalidateQueries({queryKey: ['agent-tasks']})
    },
    onError: (error, variables) => {
      // Call the passed callback with returned error and original textarea input variables
      onCreateError?.onError?.(error, variables)
    },
  })

  const query = useCAPIQuery<Task[]>({
    queryKey: ['agent-tasks', apiURL],
    queryFn: async () => {
      const response = await fetchCAPI({
        apiURL,
        path: '/agents/sessions',
      })
      const data = await response.json()
      const sessions = data.sessions

      return sortTasks(sessions as Session[])
    },
    initialData: initialTasks,
    refetchInterval: sessionsPollingInterval,
    refetchIntervalInBackground: false,
    enabled: !createSessionMutation.isPending,
  })

  // Then use those tasks to get pull requests
  const {
    data: idToPullRequestMap,
    isLoading: isLoadingPullRequests,
    isError: isErrorPullRequests,
  } = usePullRequests({tasks: query.data || [], sessionsPollingInterval, initialPullRequestsMap})

  // Combine server tasks with manually added ones
  const hydratedTasks = useMemo(() => {
    const serverHydrated = query.data && idToPullRequestMap ? hydrateTasks(query.data, idToPullRequestMap) : []

    // Remove manually added tasks that now exist in server data
    const serverIds = new Set(serverHydrated.map(t => t.resource_id))
    const filteredManualTasks = manuallyAddedTasks.filter(t => !serverIds.has(t.resource_id))

    return [...filteredManualTasks, ...serverHydrated]
  }, [query.data, idToPullRequestMap, manuallyAddedTasks])

  const createSession = (params: CreateTaskParams) => {
    // Start the mutation
    createSessionMutation.mutate({
      ...params,
    })
  }

  const isLoading =
    query.isLoading ||
    isLoadingPullRequests ||
    // keep loading=true during the small window when we have tasks but haven't yet started to fetch their pull request data
    (query.data && query.data.length > 0 && (!idToPullRequestMap || Object.keys(idToPullRequestMap).length === 0)) ||
    false
  const isError = query.isError || isErrorPullRequests

  const pendingTasks = useMutationState<PendingTask>({
    filters: {mutationKey: ['createSession'], status: 'pending'},
    select: mutation => {
      const variables = mutation.state.variables as PendingTask
      return {...variables, submittedAt: mutation.state.submittedAt}
    },
  })

  return {
    tasks: hydratedTasks,
    pendingTasks,
    isLoading,
    isError,
    createSession,
  }
}
